import React, { Component } from 'react';
import { ImageLoader } from './ImageLoader';
import './Game_Crafting.css';
import GameAPI from './GameAPI';
import {
    Game_CraftingMaterial as GameCraftingMaterial,
    getCraftingQuantity,
    getInventoryQuantity,
} from './Game_CraftingMaterial';
import { LiveCountdownBar } from './Home/CountdownBar';
import { MEDIA_VARIANTS } from '../../media';
import ItemSlot from '../../ui/ItemSlot';
import {
    buildConsumableEffectTargets,
    formatConsumableDuration,
    formatConsumableEffect,
    getConsumableDurationSeconds,
    getConsumableEffects,
    isTimedConsumableEffect,
} from './consumableEffects';
import {
    baseCampFromMutation,
    formatBuildDuration,
    normalizeBaseCamp,
} from './BaseCamp/baseCamp';

const CRAFTING_QUEUE_SIZE = 4;
let craftingInstanceSequence = 0;

const read = (value, camelName, pascalName) => (
    value?.[camelName] ?? value?.[pascalName]
);

const stationName = stationKey => ({
    workbench: 'Workbench',
    weapon_smithy: 'Weapon Smithy',
    armor_workshop: 'Armor Workshop',
    alchemy_lab: 'Alchemy Lab',
    cooking_place: 'Cooking Place',
}[stationKey] || stationKey || 'configured station');

export function matchesCraftingStation(item, stationKey) {
    const normalizedFilter = String(stationKey || '').trim().toLowerCase();
    if (!normalizedFilter) return true;
    return String(
        read(item, 'craftingStationKey', 'CraftingStationKey') || '',
    ).trim().toLowerCase() === normalizedFilter;
}

export function matchesProfession(item, professionKey) {
    const normalizedFilter = String(professionKey || '').trim().toLowerCase();
    if (!normalizedFilter) return true;
    return String(
        read(item, 'professionKey', 'ProfessionKey') || '',
    ).trim().toLowerCase() === normalizedFilter;
}

const getStationCraftingJobs = (jobs, stationKey) => (
    (jobs || []).filter(job => matchesCraftingStation(job, stationKey))
);

export function getCraftingDurationSeconds(item) {
    return Math.max(
        0,
        Number(read(
            item,
            'craftingDurationSeconds',
            'CraftingDurationSeconds',
        )) || 0,
    );
}

function entityId(value) {
    return String(read(value, 'id', 'Id') || '');
}

function getItemType(item, itemTypes) {
    const itemTypeId = String(read(item, 'typeId', 'TypeId') || '');
    return (itemTypes || []).find(itemType => (
        entityId(itemType) === itemTypeId
    ));
}

export function getSelectableCraftingRarities(item, itemTypes, rarities) {
    if (read(getItemType(item, itemTypes), 'isEquipment', 'IsEquipment') !== true) {
        return [];
    }
    const minimumRarityId = String(
        read(item, 'craftedRarityId', 'CraftedRarityId') || '',
    );
    const minimum = (rarities || []).find(rarity => (
        entityId(rarity) === minimumRarityId
    ));
    if (!minimum) return [];
    const minimumRank = Number(read(minimum, 'rank', 'Rank')) || 0;
    return (rarities || [])
        .filter(rarity => (
            entityId(rarity)
            && String(read(
                rarity,
                'salvageCatalystItemId',
                'SalvageCatalystItemId',
            ) || '')
            && (Number(read(rarity, 'rank', 'Rank')) || 0) >= minimumRank
        ))
        .sort((left, right) => (
            (Number(read(left, 'rank', 'Rank')) || 0)
            - (Number(read(right, 'rank', 'Rank')) || 0)
        ));
}

export function getCraftingMaterials(
    item,
    itemTypes,
    rarities,
    selectedRarityId,
    itemLevel = null,
) {
    const materials = read(item, 'craftingMats', 'CraftingMats') || [];
    const choices = getSelectableCraftingRarities(item, itemTypes, rarities);
    const scaleMaterials = resolvedMaterials => {
        if (!itemLevel) return resolvedMaterials;
        const authoredLevel = Math.max(1, Number(read(
            item, 'craftedItemLevel', 'CraftedItemLevel',
        )) || 1);
        return resolvedMaterials.map(material => ({
            ...material,
            amount: Math.ceil(Math.max(0, Number(read(
                material, 'amount', 'Amount',
            )) || 0) * itemLevel / authoredLevel),
        }));
    };
    if (choices.length === 0) return scaleMaterials(materials);
    const minimumRarityId = String(
        read(item, 'craftedRarityId', 'CraftedRarityId') || '',
    );
    const minimum = choices.find(rarity => entityId(rarity) === minimumRarityId);
    const selected = choices.find(rarity => (
        entityId(rarity) === String(selectedRarityId || minimumRarityId)
    ));
    if (!minimum || !selected) return scaleMaterials(materials);
    const minimumCatalystId = String(read(
        minimum,
        'salvageCatalystItemId',
        'SalvageCatalystItemId',
    ) || '');
    const selectedCatalystId = String(read(
        selected,
        'salvageCatalystItemId',
        'SalvageCatalystItemId',
    ) || '');
    const minimumCoreId = String(read(
        minimum,
        'craftingCoreItemId',
        'CraftingCoreItemId',
    ) || '');
    const selectedCoreId = String(read(
        selected,
        'craftingCoreItemId',
        'CraftingCoreItemId',
    ) || '');
    const resolved = materials.map((material) => {
        const materialId = String(read(material, 'itemId', 'ItemId') || '');
        return {
            itemId: materialId === minimumCatalystId
                ? selectedCatalystId
                : minimumCoreId && materialId === minimumCoreId
                    ? selectedCoreId
                    : materialId,
            amount: Math.max(
                0,
                Number(read(material, 'amount', 'Amount')) || 0,
            ),
        };
    }).filter(material => material.itemId);
    if (
        selectedCoreId
        && !resolved.some(material => material.itemId === selectedCoreId)
    ) {
        resolved.push({ itemId: selectedCoreId, amount: 1 });
    }
    return scaleMaterials(resolved);
}

export function getReverseEngineeredLevel(item, baseCamp) {
    const itemId = String(read(item, 'id', 'Id') || '');
    const known = read(baseCamp, 'knownRecipeIds', 'KnownRecipeIds') || [];
    if (known.some(recipeId => String(recipeId) === itemId)) {
        return Math.max(1, Number(read(
            item, 'craftedItemLevel', 'CraftedItemLevel',
        )) || 1);
    }
    const interval = Math.max(1, Number(read(
        item, 'salvagesRequiredToLearn', 'SalvagesRequiredToLearn',
    )) || 10);
    const progress = (read(
        baseCamp, 'salvageRecipeProgress', 'SalvageRecipeProgress',
    ) || []).find(entry => String(read(entry, 'itemId', 'ItemId') || '') === itemId);
    return Math.min(25, Math.floor(Math.max(0, Number(read(
        progress, 'salvageCount', 'SalvageCount',
    )) || 0) / interval));
}

export function getCraftingTimer(job, serverTimeOffsetMs = 0) {
    const startedAt = Date.parse(job?.startedAtUtc);
    const completesAt = Date.parse(job?.completesAtUtc);
    if (!Number.isFinite(startedAt) || !Number.isFinite(completesAt)) {
        return null;
    }
    const interval = (completesAt - startedAt) / 1000;
    if (interval <= 0) return null;
    const offset = Number(serverTimeOffsetMs) || 0;
    const localStartedAt = startedAt - offset;
    const localCompletesAt = completesAt - offset;
    return {
        interval,
        remaining: Math.max(0, (localCompletesAt - Date.now()) / 1000),
        updatedAt: Date.now(),
        lastAttackAt: localStartedAt,
        nextAttackAt: localCompletesAt,
    };
}

export function hasCraftingMaterials(item, inventory, storage) {
    const materials = read(item, 'craftingMats', 'CraftingMats') || [];
    return materials.every((material) => {
        const materialId = String(read(material, 'itemId', 'ItemId') || '');
        const required = Math.max(
            0,
            Number(read(material, 'amount', 'Amount')) || 0,
        );
        return (
            getCraftingQuantity(inventory, storage, materialId)
        ) >= required;
    });
}

export function getRecipeTier(item) {
    const explicitTier = Number(read(item, 'recipeTier', 'RecipeTier'));
    if (Number.isInteger(explicitTier) && explicitTier > 0) {
        return explicitTier;
    }
    const itemLevel = Number(read(
        item,
        'craftedItemLevel',
        'CraftedItemLevel',
    ));
    return Number.isFinite(itemLevel) && itemLevel > 0
        ? Math.ceil(itemLevel / 5)
        : 1;
}

function getBuildingLevel(building) {
    const configuredLevel = Number(
        read(building, 'currentLevel', 'CurrentLevel')
        ?? read(building, 'level', 'Level'),
    );
    if (Number.isFinite(configuredLevel)) {
        return Math.max(0, Math.floor(configuredLevel));
    }
    return building?.status === 'completed' || building?.Status === 'completed'
        ? 1
        : 0;
}

export function getCraftingAvailability(item, baseCamp) {
    if (!item) {
        return { available: false, reason: 'Item data is unavailable.' };
    }
    if (read(item, 'isCraftable', 'IsCraftable') === false) {
        return { available: false, reason: 'Crafting is disabled for this item.' };
    }

    const outputItemId = String(read(item, 'id', 'Id') || '');
    const unlockMode = String(
        read(item, 'recipeUnlockMode', 'RecipeUnlockMode') || 'automatic',
    ).toLowerCase();
    if (['discovered', 'salvage'].includes(unlockMode)) {
        if (!baseCamp) {
            return { available: false, reason: 'Recipe status is unavailable.' };
        }
        const knownRecipeIds = baseCamp.knownRecipeIds || [];
        const learnedLevel = getReverseEngineeredLevel(item, baseCamp);
        if (!knownRecipeIds.some(recipeId => String(recipeId) === outputItemId)
            && !(unlockMode === 'salvage' && learnedLevel > 0)) {
            if (unlockMode === 'salvage') {
                const requiredCount = Math.max(
                    1,
                    Number(read(
                        item,
                        'salvagesRequiredToLearn',
                        'SalvagesRequiredToLearn',
                    )) || 1,
                );
                const progress = (baseCamp.salvageRecipeProgress || []).find(
                    entry => String(entry?.itemId || '') === outputItemId,
                );
                const salvageCount = Math.max(
                    0, Number(progress?.salvageCount) || 0,
                );
                const nextThreshold = (Math.floor(salvageCount / requiredCount) + 1)
                    * requiredCount;
                return {
                    available: false,
                    reason: `Reverse engineering: ${salvageCount}/${nextThreshold}.`,
                };
            }
            return {
                available: false,
                reason: 'Learn this recipe from its recipe item first.',
            };
        }
    }

    const professionKey = String(
        read(item, 'professionKey', 'ProfessionKey') || '',
    );
    const requiredLevel = Math.max(
        0,
        Number(read(
            item,
            'requiredProfessionLevel',
            'RequiredProfessionLevel',
        )) || 0,
    );
    if (professionKey) {
        if (!baseCamp) {
            return { available: false, reason: 'Profession status is unavailable.' };
        }
        const profession = baseCamp.professions?.find(entry => (
            entry.professionKey === professionKey
        ));
        if (!profession) {
            return {
                available: false,
                reason: `Profession “${professionKey}” is unavailable.`,
            };
        }
        if (profession.isActive === false) {
            return {
                available: false,
                reason: `Profession “${profession.name}” is inactive.`,
            };
        }
        if (Number(profession.level) < requiredLevel) {
            return {
                available: false,
                reason: `Requires ${profession.name} level ${requiredLevel} (current: ${profession.level}).`,
            };
        }
    }

    const stationKey = String(
        read(item, 'craftingStationKey', 'CraftingStationKey') || '',
    );
    const fieldCraftable = Boolean(read(
        item,
        'fieldCraftable',
        'FieldCraftable',
    ));
    if (!stationKey) {
        if (fieldCraftable) {
            return { available: true, reason: 'Can be crafted in the field.' };
        }
        return { available: false, reason: 'No crafting station is configured.' };
    }
    if (!baseCamp) {
        return { available: false, reason: 'Camp building status is unavailable.' };
    }
    const station = baseCamp.buildings?.find(building => (
        String(building.buildingKey || '').toLowerCase()
            === stationKey.toLowerCase()
        || String(building.craftingStationKey || '').toLowerCase()
            === stationKey.toLowerCase()
    ));
    const stationLevel = getBuildingLevel(station);
    if (stationLevel <= 0) {
        return {
            available: false,
            reason: `Build the ${stationName(stationKey)} in Camp first.`,
        };
    }
    const recipeTier = unlockMode === 'salvage' ? 1 : getRecipeTier(item);
    if (stationLevel < recipeTier) {
        return {
            available: false,
            reason: `Requires ${stationName(stationKey)} level ${recipeTier} (current: ${stationLevel}).`,
        };
    }
    if (fieldCraftable) {
        return {
            available: true,
            reason: `Field crafting unlocked by ${stationName(stationKey)} level ${stationLevel}.`,
        };
    }
    if (!baseCamp?.isInCamp) {
        return {
            available: false,
            reason: `Enter Camp and use the ${stationName(stationKey)}.`,
        };
    }
    return {
        available: true,
        reason: `Available at the level ${stationLevel} ${stationName(stationKey)}.`,
    };
}

export function shouldShowCraftingRecipe(item, baseCamp, inventory) {
    if (!item) return false;

    const professionKey = String(
        read(item, 'professionKey', 'ProfessionKey') || '',
    );
    const requiredProfessionLevel = Math.max(
        0,
        Number(read(
            item,
            'requiredProfessionLevel',
            'RequiredProfessionLevel',
        )) || 0,
    );
    if (professionKey) {
        const profession = (read(
            baseCamp,
            'professions',
            'Professions',
        ) || []).find(entry => (
            String(read(
                entry,
                'professionKey',
                'ProfessionKey',
            ) || '') === professionKey
        ));
        if (!profession || Number(read(
            profession,
            'level',
            'Level',
        )) < requiredProfessionLevel) {
            return false;
        }
    }

    const outputItemId = String(read(item, 'id', 'Id') || '');
    const knownRecipeIds = read(
        baseCamp,
        'knownRecipeIds',
        'KnownRecipeIds',
    ) || [];
    if (knownRecipeIds.some(recipeId => String(recipeId) === outputItemId)) {
        return true;
    }

    const unlockMode = String(
        read(item, 'recipeUnlockMode', 'RecipeUnlockMode') || 'automatic',
    ).toLowerCase();
    if (unlockMode === 'automatic') return true;
    if (unlockMode === 'discovered') {
        const recipeItemId = String(
            read(item, 'recipeItemId', 'RecipeItemId') || '',
        );
        return recipeItemId !== ''
            && getInventoryQuantity(inventory, recipeItemId) > 0;
    }
    if (unlockMode === 'salvage') {
        const progress = read(
            baseCamp,
            'salvageRecipeProgress',
            'SalvageRecipeProgress',
        ) || [];
        return progress.some(entry => (
            String(read(entry, 'itemId', 'ItemId') || '') === outputItemId
            && Number(read(entry, 'salvageCount', 'SalvageCount')) > 0
        ));
    }
    return false;
}

function getStationItemLevelCap(item, baseCamp) {
    const stationKey = String(read(
        item, 'craftingStationKey', 'CraftingStationKey',
    ) || '');
    const station = (baseCamp?.buildings || []).find(building => (
        String(building.buildingKey || '').toLowerCase()
            === stationKey.toLowerCase()
    ));
    return Math.max(1, getBuildingLevel(station) * 5);
}

export class GameCrafting extends Component {
    titleId = `game-crafting-title-${craftingInstanceSequence += 1}`;

    state = {
        items: [],
        baseCamp: null,
        consumableEffectTargets: buildConsumableEffectTargets(),
        itemTypes: [],
        rarities: [],
        selectedRarityIds: {},
        selectedItemLevels: {},
        loading: true,
        error: '',
        submitting: false,
        message: '',
    };

    componentDidMount() {
        this.mounted = true;
        this.load();
    }

    componentDidUpdate(previousProps, previousState) {
        if (
            this.props.baseCamp
            && this.props.baseCamp !== previousProps.baseCamp
        ) {
            const nextBaseCamp = normalizeBaseCamp(this.props.baseCamp);
            if (
                !this.state.baseCamp
                || Number(nextBaseCamp?.generatedAtMs || 0)
                    >= Number(this.state.baseCamp.generatedAtMs || 0)
            ) {
                this.setState({ baseCamp: nextBaseCamp });
            }
        }
        if (this.state.baseCamp !== previousState.baseCamp) {
            this.scheduleCraftingRefresh();
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        window.clearTimeout(this.refreshTimeout);
    }

    load = async () => {
        this.setState({ loading: true, error: '', message: '' });
        try {
            const [
                items,
                baseCampPayload,
                effectTargetCatalog,
                itemTypes,
                rarities,
            ] = await Promise.all([
                GameAPI.fetchCraftableItems(),
                GameAPI.fetchBaseCamp(),
                GameAPI.fetchConsumableEffectTargets(),
                GameAPI.fetchItemTypes(),
                GameAPI.fetchRarityCatalog(),
            ]);
            const craftableItems = Array.isArray(items) ? items : [];
            if (!Array.isArray(itemTypes) || !Array.isArray(rarities)) {
                throw new Error('Crafting quality data could not be loaded.');
            }
            if (!this.mounted) return;
            this.setState({
                items: craftableItems,
                itemTypes,
                rarities,
                baseCamp: normalizeBaseCamp(baseCampPayload),
                consumableEffectTargets:
                    buildConsumableEffectTargets(effectTargetCatalog),
            });
        } catch (error) {
            if (!this.mounted) return;
            this.setState({
                error: error?.message || 'Crafting data could not be loaded.',
            });
        } finally {
            if (this.mounted) this.setState({ loading: false });
        }
    };

    refreshCraftingState = async () => {
        try {
            const payload = await GameAPI.fetchBaseCamp();
            if (!this.mounted) return;
            this.setState({
                baseCamp: normalizeBaseCamp(payload),
                error: '',
            });
            this.props.onMutation?.(payload);
        } catch (error) {
            if (!this.mounted) return;
            this.setState({
                error: error?.message || 'Crafting status could not be refreshed.',
            });
        }
    };

    scheduleCraftingRefresh = () => {
        window.clearTimeout(this.refreshTimeout);
        const activeJob = getStationCraftingJobs(
            this.state.baseCamp?.craftingQueue,
            this.props.stationKey,
        )[0];
        if (!activeJob) return;
        const serverNow = Date.now()
            + Number(this.state.baseCamp.serverTimeOffsetMs || 0);
        const completesAt = Date.parse(activeJob.completesAtUtc);
        const delay = activeJob.ready || !Number.isFinite(completesAt)
            ? 5000
            : Math.max(150, completesAt - serverNow + 150);
        this.refreshTimeout = window.setTimeout(
            this.refreshCraftingState,
            delay,
        );
    };

    onDragStart = (item) => {
        this.props.onDragStart?.({
            item: {
                itemId: item.id,
                quantity: 1,
                itemTypeId: item.typeId,
            },
            rowIndex: -1,
            colIndex: -1,
            fromComponent: 'crafting',
        });
    };

    onCraft = async (item) => {
        const availability = getCraftingAvailability(item, this.state.baseCamp);
        const itemStationKey = read(
            item,
            'craftingStationKey',
            'CraftingStationKey',
        ) || this.props.stationKey;
        const jobs = getStationCraftingJobs(
            this.state.baseCamp?.craftingQueue,
            itemStationKey,
        );
        const inventory = Array.isArray(this.state.baseCamp?.inventory)
            ? this.state.baseCamp.inventory
            : this.props.inventory;
        const storage = this.state.baseCamp?.storage?.entries;
        const itemId = read(item, 'id', 'Id');
        const selectableRarities = getSelectableCraftingRarities(
            item,
            this.state.itemTypes,
            this.state.rarities,
        );
        const rarityId = selectableRarities.length > 0
            ? this.state.selectedRarityIds[itemId]
                || read(item, 'craftedRarityId', 'CraftedRarityId')
                || ''
            : '';
        const learnedLevel = getReverseEngineeredLevel(item, this.state.baseCamp);
        const maximumItemLevel = Math.min(
            Math.max(1, learnedLevel),
            getStationItemLevelCap(item, this.state.baseCamp),
        );
        const itemLevel = Math.min(
            this.state.selectedItemLevels[itemId] || maximumItemLevel,
            maximumItemLevel,
        );
        const craftingMaterials = getCraftingMaterials(
            item,
            this.state.itemTypes,
            this.state.rarities,
            rarityId,
            itemLevel,
        );
        if (
            !availability.available
            || this.state.submitting
            || jobs.length >= CRAFTING_QUEUE_SIZE + 1
            || (Array.isArray(inventory)
                && !hasCraftingMaterials(
                    { craftingMats: craftingMaterials },
                    inventory,
                    storage,
                ))
        ) return;
        this.setState({ submitting: true, error: '', message: '' });
        try {
            const levelSelectable = String(read(
                item, 'recipeUnlockMode', 'RecipeUnlockMode',
            ) || '').toLowerCase() === 'salvage';
            const options = {};
            if (rarityId) options.rarityId = rarityId;
            if (levelSelectable) options.itemLevel = itemLevel;
            const result = Object.keys(options).length > 0
                ? await GameAPI.craftItem(itemId, options)
                : await GameAPI.craftItem(itemId);
            if (!this.mounted) return;
            const nextBaseCamp = baseCampFromMutation(result);
            const rarity = this.state.rarities.find(candidate => (
                entityId(candidate) === String(rarityId)
            ));
            const rarityName = read(rarity, 'name', 'Name');
            this.setState({
                ...(nextBaseCamp ? { baseCamp: nextBaseCamp } : {}),
                message: `${getCraftingDurationSeconds(item) > 0
                    ? 'Queued'
                    : 'Crafted'} “${item.name}”${rarityName
                    ? ` at ${rarityName} rarity`
                    : ''} successfully.`,
            });
            if (nextBaseCamp) this.props.onMutation?.(result);
        } catch (error) {
            if (!this.mounted) return;
            this.setState({
                error: error?.message || `“${item.name}” could not be crafted.`,
            });
        } finally {
            if (this.mounted) this.setState({ submitting: false });
        }
    };

    render() {
        const {
            baseCamp,
            consumableEffectTargets,
            error,
            itemTypes,
            items,
            loading,
            message,
            rarities,
            selectedRarityIds,
            selectedItemLevels,
            submitting,
        } = this.state;
        const stationKey = String(this.props.stationKey || '').trim();
        const inventory = Array.isArray(baseCamp?.inventory)
            ? baseCamp.inventory
            : this.props.inventory;
        const storage = baseCamp?.storage?.entries;
        const visibleItems = items.filter(item => (
            matchesCraftingStation(item, stationKey)
            && matchesProfession(item, this.props.professionKey)
            && shouldShowCraftingRecipe(item, baseCamp, inventory)
        ));
        const title = stationKey
            ? (this.props.stationName || stationName(stationKey))
            : 'Crafting';
        const craftingJobs = getStationCraftingJobs(
            baseCamp?.craftingQueue,
            stationKey,
        );
        const activeJob = craftingJobs[0];
        const craftingQueue = craftingJobs.slice(1);
        const craftingItem = items.find(item => (
            String(read(item, 'id', 'Id')) === String(activeJob?.itemId)
        ));
        const isCooking = String(read(
            craftingItem,
            'professionKey',
            'ProfessionKey',
        ) || '') === 'cooking';
        const activeRarity = rarities.find(rarity => (
            entityId(rarity) === String(activeJob?.rarityId || '')
        ));
        const activeRarityName = read(activeRarity, 'name', 'Name');
        const craftingTimer = getCraftingTimer(
            activeJob,
            baseCamp?.serverTimeOffsetMs,
        );
        return (
            <section
                aria-busy={loading || submitting}
                aria-label={`${title} crafting`}
                className="game-Crafting"
            >
                <div className="game-crafting__overview">
                    <section
                        aria-labelledby={`${this.titleId}-queue`}
                        className="game-crafting__queue"
                    >
                        <h4 id={`${this.titleId}-queue`}>
                            Queue <span>{craftingQueue.length}/{CRAFTING_QUEUE_SIZE}</span>
                        </h4>
                        {craftingQueue.length > 0 ? (
                            <ol className="game-crafting__queue-slots">
                                {craftingQueue.map((queuedSelection, index) => {
                                    const queuedItemId = typeof queuedSelection === 'string'
                                        ? queuedSelection
                                        : queuedSelection?.itemId;
                                    const queuedRarityId = typeof queuedSelection === 'string'
                                        ? ''
                                        : queuedSelection?.rarityId;
                                    const queuedItem = items.find(item => (
                                        String(read(item, 'id', 'Id'))
                                        === String(queuedItemId)
                                    ));
                                    const queuedRarity = rarities.find(rarity => (
                                        entityId(rarity) === String(queuedRarityId || '')
                                    ));
                                    const queuedLabel = queuedItem
                                        ? `${queuedItem.name}${queuedRarity
                                            ? ` · ${read(queuedRarity, 'name', 'Name')}`
                                            : ''}`
                                        : 'Unknown recipe';
                                    return (
                                        <ItemSlot
                                            aria-label={`Queue slot ${index + 1}: ${queuedLabel}`}
                                            as="li"
                                            className="game-crafting__queue-slot"
                                            item={queuedItem}
                                            itemType={getItemType(queuedItem, itemTypes)}
                                            key={queuedSelection?.operationId || queuedItemId || index}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className="game-crafting__queue-number"
                                            >
                                                {index + 1}
                                            </span>
                                            {queuedItem ? (
                                                <ImageLoader
                                                    alt=""
                                                    className="game-crafting__queue-image"
                                                    imageId={queuedItem.imageId}
                                                    animatedMediaId={queuedItem.animatedMediaId || queuedItem.animatedImageId}
                                                    staticPreview
                                                    variant={MEDIA_VARIANTS.CARD}
                                                />
                                            ) : null}
                                        </ItemSlot>
                                    );
                                })}
                            </ol>
                        ) : null}
                    </section>
                    <section
                        aria-labelledby={`${this.titleId}-active`}
                        className="game-crafting__active"
                    >
                        <h4 id={`${this.titleId}-active`}>At the station</h4>
                        {craftingItem ? (
                            <div className="game-crafting__active-recipe">
                                <ImageLoader
                                    alt=""
                                    className="game-crafting__active-image"
                                    imageId={craftingItem.imageId}
                                    animatedMediaId={craftingItem.animatedMediaId || craftingItem.animatedImageId}
                                    staticPreview
                                    variant={MEDIA_VARIANTS.CARD}
                                />
                                {craftingTimer ? (
                                    <>
                                        <LiveCountdownBar
                                            label={`${isCooking ? 'Cooking' : 'Crafting'} “${craftingItem.name}”${activeRarityName ? ` at ${activeRarityName} rarity` : ''}`}
                                            timer={craftingTimer}
                                            variant="crafting"
                                        />
                                        {activeJob.ready ? (
                                            <p>Ready — make Pack space to collect it.</p>
                                        ) : null}
                                    </>
                                ) : (
                                    <p>
                                        {craftingItem.name}
                                        {activeRarityName
                                            ? ` · ${activeRarityName}`
                                            : ''}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="game-crafting__idle">Station idle</p>
                        )}
                    </section>
                </div>
                {loading ? <p role="status">Loading crafting options…</p> : null}
                {error ? (
                    <div className="game-crafting__error" role="alert">
                        <p>{error}</p>
                        {visibleItems.length === 0 ? (
                            <button onClick={this.load} type="button">Try again</button>
                        ) : null}
                    </div>
                ) : null}
                <p
                    aria-live="polite"
                    className="game-crafting__message"
                    role="status"
                >
                    {message}
                </p>
                {!loading && !error && visibleItems.length === 0 ? (
                    <p className="game-crafting__empty" role="status">
                        {stationKey
                            ? `No learned or learnable ${stationName(stationKey)} recipes yet.`
                            : 'No learned or learnable crafting recipes yet.'}
                    </p>
                ) : null}
                <div className="game-crafting__list">
                    {visibleItems.map((item) => {
                        const availability = getCraftingAvailability(item, baseCamp);
                        const busy = craftingJobs.some(job => (
                            String(job.itemId) === String(item.id)
                        ));
                        const durationSeconds =
                            getCraftingDurationSeconds(item);
                        const craftedRarityId = String(
                            read(item, 'craftedRarityId', 'CraftedRarityId') || '',
                        );
                        const selectableRarities = getSelectableCraftingRarities(
                            item,
                            itemTypes,
                            rarities,
                        );
                        const selectedRarityId = String(
                            selectedRarityIds[item.id]
                            || craftedRarityId,
                        );
                        const minimumRarity = rarities.find(rarity => (
                            entityId(rarity) === craftedRarityId
                        ));
                        const selectedRarity = rarities.find(rarity => (
                            entityId(rarity) === selectedRarityId
                        ));
                        const isEquipment = read(
                            getItemType(item, itemTypes),
                            'isEquipment',
                            'IsEquipment',
                        ) === true;
                        const learnedItemLevel = isEquipment
                            ? getReverseEngineeredLevel(item, baseCamp)
                            : 0;
                        const levelSelectable = isEquipment && String(read(
                            item, 'recipeUnlockMode', 'RecipeUnlockMode',
                        ) || '').toLowerCase() === 'salvage';
                        const maximumItemLevel = Math.min(
                            Math.max(1, learnedItemLevel),
                            getStationItemLevelCap(item, baseCamp),
                        );
                        const selectedItemLevel = Math.min(
                            selectedItemLevels[item.id] || maximumItemLevel,
                            maximumItemLevel,
                        );
                        const outputRarityId = isEquipment
                            ? craftedRarityId
                            : String(
                                read(item, 'rarityId', 'RarityId')
                                || craftedRarityId,
                            );
                        const outputRarity = String(
                            (outputRarityId === craftedRarityId
                                && read(item, 'craftedRarityName', 'CraftedRarityName'))
                            || read(
                                rarities.find(rarity => (
                                    entityId(rarity) === outputRarityId
                                )),
                                'name',
                                'Name',
                            )
                            || GameAPI.fetchRarityCached(outputRarityId)?.name
                            || '',
                        );
                        const craftingMaterials = getCraftingMaterials(
                            item,
                            itemTypes,
                            rarities,
                            selectedRarityId,
                            levelSelectable ? selectedItemLevel : null,
                        );
                        const outputEffects = getConsumableEffects(item);
                        const effectDuration = formatConsumableDuration(
                            getConsumableDurationSeconds(item),
                        );
                        const hasMaterials = !Array.isArray(inventory)
                            || hasCraftingMaterials(
                                { craftingMats: craftingMaterials },
                                inventory,
                                storage,
                            );
                        const itemStationKey = read(
                            item,
                            'craftingStationKey',
                            'CraftingStationKey',
                        ) || stationKey;
                        const itemQueueFull = getStationCraftingJobs(
                            baseCamp?.craftingQueue,
                            itemStationKey,
                        ).length >= CRAFTING_QUEUE_SIZE + 1;
                        const blocker = !availability.available
                            ? `Locked · ${availability.reason}`
                            : !hasMaterials
                                ? 'Not enough materials.'
                                : itemQueueFull
                                    ? 'Crafting queue is full.'
                                    : '';
                        const statusId = blocker
                            ? `crafting-status-${item.id}`
                            : undefined;
                        return (
                            <article
                                className={`game-item${availability.available ? '' : ' is-locked'}`}
                                draggable={Boolean(this.props.onDragStart) && availability.available}
                                key={item.id}
                                onDragStart={this.props.onDragStart
                                    ? () => this.onDragStart(item)
                                    : undefined}
                            >
                                <h4 className="game-crafting__recipe-title">
                                    {item.name}
                                </h4>
                                <div className="game-crafting__recipe-intro">
                                    <ImageLoader
                                        alt=""
                                        className="craftableItemImage"
                                        imageId={item.imageId}
                                        animatedMediaId={item.animatedMediaId || item.animatedImageId}
                                        variant={MEDIA_VARIANTS.CARD}
                                    />
                                    {item.flavourText ? <p>{item.flavourText}</p> : null}
                                </div>
                                <div className="craftableItemDescription">
                                    {levelSelectable ? (
                                        <div className="game-crafting__level">
                                            <label htmlFor={`${this.titleId}-level-${item.id}`}>
                                                Craft item level
                                            </label>
                                            <select
                                                id={`${this.titleId}-level-${item.id}`}
                                                onChange={event => this.setState(previousState => ({
                                                    selectedItemLevels: {
                                                        ...previousState.selectedItemLevels,
                                                        [item.id]: Number(event.target.value),
                                                    },
                                                }))}
                                                value={selectedItemLevel}
                                            >
                                                {Array.from(
                                                    { length: maximumItemLevel },
                                                    (_, index) => index + 1,
                                                ).map(level => (
                                                    <option key={level} value={level}>
                                                        Level {level}
                                                    </option>
                                                ))}
                                            </select>
                                            <small>
                                                Reverse engineered through level {learnedItemLevel}.
                                                Materials scale with the selected level.
                                            </small>
                                        </div>
                                    ) : null}
                                    {selectableRarities.length > 0 ? (
                                        <div className="game-crafting__rarity">
                                            <label htmlFor={`${this.titleId}-rarity-${item.id}`}>
                                                Craft rarity
                                            </label>
                                            <select
                                                aria-describedby={`${this.titleId}-rarity-help-${item.id}`}
                                                id={`${this.titleId}-rarity-${item.id}`}
                                                onChange={event => this.setState(
                                                    previousState => ({
                                                        selectedRarityIds: {
                                                            ...previousState.selectedRarityIds,
                                                            [item.id]: event.target.value,
                                                        },
                                                    }),
                                                )}
                                                value={selectedRarityId}
                                            >
                                                {selectableRarities.map(rarity => (
                                                    <option
                                                        key={entityId(rarity)}
                                                        value={entityId(rarity)}
                                                    >
                                                        {read(rarity, 'name', 'Name')}
                                                    </option>
                                                ))}
                                            </select>
                                            <small id={`${this.titleId}-rarity-help-${item.id}`}>
                                                One matching catalyst guarantees
                                                {' '}
                                                {read(selectedRarity, 'name', 'Name')}
                                                {' '}rarity
                                                {read(
                                                    selectedRarity,
                                                    'craftingCoreItemId',
                                                    'CraftingCoreItemId',
                                                )
                                                    ? '; one additional Core is required'
                                                    : ''}
                                                ; attributes still roll.
                                            </small>
                                        </div>
                                    ) : null}
                                    <GameCraftingMaterial
                                        craftingMaterial={craftingMaterials}
                                        inventory={inventory}
                                        storage={storage}
                                    />
                                    {outputEffects.length > 0 ? (
                                        <div className="game-crafting__effects">
                                            <span>Result</span>
                                            <ul>
                                                {outputEffects.map((effect, index) => (
                                                    <li key={`${read(effect, 'type', 'Type')}-${index}`}>
                                                        {formatConsumableEffect(
                                                            effect,
                                                            consumableEffectTargets,
                                                        )}
                                                        {isTimedConsumableEffect(effect)
                                                        && effectDuration
                                                            ? ` for ${effectDuration}`
                                                            : ''}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : null}
                                    {durationSeconds > 0 || outputRarity ? (
                                        <p className="game-crafting__meta">
                                            {durationSeconds > 0 ? (
                                                <span>
                                                    Time: {formatBuildDuration(durationSeconds)}
                                                </span>
                                            ) : null}
                                            {outputRarity ? (
                                                <span>
                                                    {isEquipment
                                                        ? 'Minimum rarity'
                                                        : 'Output rarity'}: {outputRarity}
                                                </span>
                                            ) : null}
                                        </p>
                                    ) : null}
                                    {blocker ? (
                                        <p
                                            className="game-crafting__availability"
                                            id={statusId}
                                        >
                                            {blocker}
                                        </p>
                                    ) : null}
                                    <button
                                        aria-describedby={statusId}
                                        disabled={
                                            Boolean(blocker) || submitting
                                        }
                                        onClick={() => this.onCraft(item)}
                                        type="button"
                                    >
                                        {busy ? 'Queue again' : 'Craft'}
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        );
    }
}
